import { useState, useCallback, useRef } from 'react';
import { Platform } from '../../../types/Platforms';
import { Post } from '../../../types/postTypes';
import { generatePostByMedia, saveChatVideos } from '../../../services/contentService';
import { fetchPostsByIds, generateReviewLink } from '../../../services/postService';
import { mapAllowedPlatforms } from '../../../utils/allowedPlatformMapping';
import { ConnectedProfile } from '../../../types/customer';
import { debug } from "../../../utils/debugLogger";
import {
  VideoForPlatform,
  PlatformDescription,
  SaveToPlatformStep,
} from '../types';

interface UseSaveToPlatformProps {
  customerId: string;
  idToken: string;
  agentId: string;
  connectedProfiles?: ConnectedProfile[];
  /** Optional initial platforms to pre-select when opening the panel */
  initialSelectedPlatforms?: Platform[];
}

interface UseSaveToPlatformReturn {
  step: SaveToPlatformStep;
  selectedPlatforms: Platform[];
  descriptions: PlatformDescription[];
  createdPostIds: string[];
  reviewUrl: string | null;
  isGeneratingDescriptions: boolean;
  isSaving: boolean;
  isGeneratingReviewLink: boolean;
  error: string | null;
  thumbnailUrl: string | null;

  togglePlatform: (platform: Platform) => void;
  generateDescriptions: (video: VideoForPlatform) => Promise<void>;
  updateDescription: (platform: Platform, updates: Partial<PlatformDescription>) => void;
  setThumbnail: (url: string) => void;
  saveToReviewPosts: (video: VideoForPlatform) => Promise<void>;
  generateReview: () => Promise<void>;
  reset: () => void;
}

export const useSaveToPlatform = ({
  customerId,
  idToken,
  agentId,
  connectedProfiles,
  initialSelectedPlatforms = [],
}: UseSaveToPlatformProps): UseSaveToPlatformReturn => {
  const [step, setStep] = useState<SaveToPlatformStep>('select_platforms');
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>(initialSelectedPlatforms);
  const [descriptions, setDescriptions] = useState<PlatformDescription[]>([]);
  const [createdPostIds, setCreatedPostIds] = useState<string[]>([]);
  const [reviewUrl, setReviewUrl] = useState<string | null>(null);
  const [isGeneratingDescriptions, setIsGeneratingDescriptions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingReviewLink, setIsGeneratingReviewLink] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const togglePlatform = useCallback((platform: Platform) => {
    setSelectedPlatforms((prev) => {
      if (prev.includes(platform)) {
        return prev.filter((p) => p !== platform);
      }
      return [...prev, platform];
    });
  }, []);

  const generateDescriptions = useCallback(
    async (video: VideoForPlatform) => {
      if (selectedPlatforms.length === 0) {
        setError('Please select at least one platform');
        return;
      }

      setIsGeneratingDescriptions(true);
      setError(null);
      setStep('generating_descriptions');

      try {
        // Map platforms to business profile IDs
        const allowedPlatforms = mapAllowedPlatforms(
          selectedPlatforms,
          connectedProfiles || []
        );

        // Call the AI agent to generate platform-specific descriptions
        // When pre-generated caption exists, use it; otherwise use script as context
        const response = await generatePostByMedia(
          '', // No image URL for video posts
          video.videoUrl,
          agentId,
          video.generatedCaption || video.script || '', // Pre-generated caption or script context
          customerId,
          '', // No scheduled time
          idToken,
          allowedPlatforms
        );

        // Extract post IDs from response
        const postIds = (response.platforms || [])
          .map((p: any) => p.post_id)
          .filter(Boolean);

        if (postIds.length === 0) {
          throw new Error('No posts were created. Please try again.');
        }

        // Poll for posts until they have content
        let attempts = 0;
        const maxAttempts = 20;
        const pollInterval = 2000;

        const pollForPosts = (): Promise<Post[]> => {
          return new Promise((resolve, reject) => {
            const poll = async () => {
              attempts++;
              try {
                const posts = await fetchPostsByIds(customerId, postIds, idToken);
                // Check if posts have content populated
                const allReady = posts.length > 0 && posts.every((p) => p.content && p.content.trim().length > 0);

                if (allReady) {
                  resolve(posts);
                  return;
                }

                if (attempts >= maxAttempts) {
                  // Return whatever we have after max attempts
                  resolve(posts);
                  return;
                }

                pollingRef.current = setTimeout(poll, pollInterval);
              } catch (err) {
                if (attempts >= maxAttempts) {
                  reject(err);
                } else {
                  pollingRef.current = setTimeout(poll, pollInterval);
                }
              }
            };

            poll();
          });
        };

        const posts = await pollForPosts();

        // Convert posts to PlatformDescription objects
        const platformDescriptions: PlatformDescription[] = posts.map((post) => ({
          platform: post.platform as Platform,
          title: post.title || '',
          content: post.content || '',
          hashtags: post.hashtags ? post.hashtags.split(/[,\s]+/).filter(Boolean) : [],
          callToAction: post.call_to_action || '',
          postId: post.post_id,
        }));

        setDescriptions(platformDescriptions);
        setCreatedPostIds(postIds);
        setStep('edit_descriptions');
      } catch (err: any) {
        debug.error('Error generating descriptions:', err);
        setError(err.message || 'Failed to generate descriptions');
        setStep('select_platforms');
      } finally {
        setIsGeneratingDescriptions(false);
      }
    },
    [selectedPlatforms, connectedProfiles, agentId, customerId, idToken]
  );

  const updateDescription = useCallback(
    (platform: Platform, updates: Partial<PlatformDescription>) => {
      setDescriptions((prev) =>
        prev.map((d) =>
          d.platform === platform ? { ...d, ...updates } : d
        )
      );
    },
    []
  );

  const setThumbnail = useCallback((url: string) => {
    setThumbnailUrl(url);
  }, []);

  const saveToReviewPosts = useCallback(
    async (video: VideoForPlatform) => {
      if (descriptions.length === 0) {
        setError('No descriptions to save');
        return;
      }

      setIsSaving(true);
      setError(null);
      setStep('saving');

      try {
        const videosToSave = descriptions.map((desc) => ({
          platform: desc.platform,
          title: desc.title,
          content: desc.content,
          video_url: video.videoUrl,
          hashtags: desc.hashtags,
          call_to_action: desc.callToAction,
        }));

        const result = await saveChatVideos(customerId, videosToSave, idToken);
        setCreatedPostIds(result.post_ids || []);
        setStep('success');
      } catch (err: any) {
        debug.error('Error saving to review posts:', err);
        setError(err.message || 'Failed to save videos');
        setStep('edit_descriptions');
      } finally {
        setIsSaving(false);
      }
    },
    [descriptions, customerId, idToken]
  );

  const generateReview = useCallback(async () => {
    if (createdPostIds.length === 0) {
      setError('No posts to generate review for');
      return;
    }

    setIsGeneratingReviewLink(true);
    setError(null);

    try {
      const posts = createdPostIds.map((postId) => ({
        customer_id: customerId,
        post_id: postId,
      }));

      const response = await generateReviewLink(posts, idToken);
      setReviewUrl(response.review_url);
    } catch (err: any) {
      debug.error('Error generating review link:', err);
      setError(err.message || 'Failed to generate review link');
    } finally {
      setIsGeneratingReviewLink(false);
    }
  }, [createdPostIds, customerId, idToken]);

  const reset = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
    }
    setStep('select_platforms');
    setSelectedPlatforms([]);
    setDescriptions([]);
    setCreatedPostIds([]);
    setReviewUrl(null);
    setIsGeneratingDescriptions(false);
    setIsSaving(false);
    setIsGeneratingReviewLink(false);
    setError(null);
    setThumbnailUrl(null);
  }, []);

  return {
    step,
    selectedPlatforms,
    descriptions,
    createdPostIds,
    reviewUrl,
    isGeneratingDescriptions,
    isSaving,
    isGeneratingReviewLink,
    error,
    thumbnailUrl,

    togglePlatform,
    generateDescriptions,
    updateDescription,
    setThumbnail,
    saveToReviewPosts,
    generateReview,
    reset,
  };
};
